/**
 * USE-HELP-RENDER-CONTEXT.UTILS
 * Values of the account (thresholds, colors, groups) that replace the markers written in a guide :
 * shared by the window of the guide and by the copy of a section
 */
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { selectSession } from "@/redux/_session.selector"
import { Session } from "@/redux/_session.types"
import { HelpAccountColors } from "@/utils/help-icons-render.utils"
import { buildRepartitionGroupsHtml, HelpValues } from "@/utils/help-values.utils"
import { getRepartitionCeils, getRepartitionGroups } from "@/utils/get-repartition-ceils.utils"

//Colors of the results of the account (the default ones when it did not choose its own)
export function getHelpAccountColors(session: Session): HelpAccountColors {
  const settings = session.dashboardDisplaySettings
  const isHeatmapCustom = settings.dashboardNightmareModeHeatmap
  return {
    low: isHeatmapCustom ? settings.dashboardNightmareColor1 : "#f1a8a3",
    middle: isHeatmapCustom ? settings.dashboardNightmareColor2 : "#f8cf94",
    high: isHeatmapCustom ? settings.dashboardNightmareColor3 : "#99efc2"
  }
}

export function getHelpValues(session: Session, isGollac: boolean): HelpValues {
  const settings = session.dashboardDisplaySettings
  return {
    lowCeil: settings.dashboardNightmareModeMin,
    highCeil: settings.dashboardNightmareModeMax,
    gollacCeil: getRepartitionCeils(session, true, false)[0],
    gapLow: session.accountOptions.ceilsGollacMinValue,
    gapHigh: session.accountOptions.ceilsGollacMaxValue,
    participationMin: session.participationMin,
    groupsHtml: buildRepartitionGroupsHtml(getRepartitionGroups(session, isGollac, false))
  }
}

export function useHelpRenderContext(isGollac: boolean): { values: HelpValues; accountColors: HelpAccountColors } {
  const session = useSelector(selectSession)
  const settings = session.dashboardDisplaySettings

  const accountColors = useMemo(
    () => getHelpAccountColors(session),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      settings.dashboardNightmareModeHeatmap,
      settings.dashboardNightmareColor1,
      settings.dashboardNightmareColor2,
      settings.dashboardNightmareColor3
    ]
  )

  const values = useMemo(
    () => getHelpValues(session, isGollac),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, settings.dashboardNightmareModeMin, settings.dashboardNightmareModeMax, isGollac]
  )

  return { values, accountColors }
}
